import React from 'react';
import { CheckCircle, Award, Users, MapPin } from 'lucide-react';
import { companyInfo, stats, suppliers } from '../data/mock';

const AboutSection = () => {
  return (
    <section id="about" className="section-padding bg-[#FDFBF7]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block text-[#B8956B] font-medium text-sm uppercase tracking-[0.2em] mb-4">
            About Us
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#252A32] mb-6 heading-display">
            Engineering Excellence Since <span className="text-[#B8956B] italic">1995</span>
          </h2>
          <div className="elegant-divider mx-auto mb-6" />
          <p className="text-[#404752] text-lg leading-relaxed">
            Becone Engineering is a premier elevator manufacturing and installation company 
            specializing in customized elevator solutions for residential, commercial, and industrial clients.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Image Side */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1735178181188-855a87e83dca?w=800&q=80" 
                alt="Modern Elevator Interior"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#252A32]/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#B8956B] flex items-center justify-center">
                      <Award className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-[#252A32] font-bold text-xl">IS 17900:2022</p>
                      <p className="text-[#404752] text-sm">Safety Standards Certified</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -top-8 -right-8 w-36 h-36 border border-[#B8956B]/20 rounded-2xl -z-10" />
            <div className="absolute -bottom-8 -left-8 w-28 h-28 bg-[#B8956B]/5 rounded-2xl -z-10" />
          </div>

          {/* Text Side */}
          <div>
            <h3 className="text-2xl md:text-3xl font-semibold text-[#252A32] mb-6 heading-display">
              Premium Engineering Segment Specialists
            </h3>
            <p className="text-[#404752] mb-6 leading-relaxed">
              At Becone Elevators, we take pride in our expertise across the complete elevator lifecycle - 
              from innovative design and precision manufacturing to professional installation and comprehensive 
              commissioning services.
            </p>
            <p className="text-[#404752] mb-8 leading-relaxed">
              Our safety-first philosophy drives everything we do. We ensure every installation complies 
              with IS 17900:2022 standards while delivering customized solutions that perfectly match your 
              architectural vision and functional requirements.
            </p>

            {/* Key Points */}
            <div className="space-y-4 mb-8">
              {[
                'Customized elevator solutions tailored to your needs',
                'Premium quality components from trusted suppliers',
                'Comprehensive service coverage across Kerala',
                'GST registered professional organization'
              ].map((point, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#B8956B]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-3.5 h-3.5 text-[#B8956B]" />
                  </div>
                  <span className="text-[#404752]">{point}</span>
                </div>
              ))}
            </div>

            {/* Location */}
            <div className="flex items-start gap-3 p-5 bg-white rounded-xl border border-gray-100">
              <MapPin className="w-5 h-5 text-[#B8956B] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-[#252A32]">Service Coverage</p>
                <p className="text-[#404752] text-sm">{companyInfo.address.full}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="stat-item text-center p-10 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-[#B8956B]/20 transition-colors"
            >
              <div className="text-4xl md:text-5xl font-semibold text-[#252A32] mb-2 heading-display">{stat.value}</div>
              <div className="text-[#404752] font-medium text-sm uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Suppliers */}
        <div className="text-center">
          <p className="text-[#404752] text-xs uppercase tracking-[0.2em] mb-8">Trusted Component Suppliers</p>
          <div className="flex flex-wrap justify-center items-center gap-6">
            {suppliers.map((supplier, index) => (
              <div 
                key={index}
                className="px-6 py-3 bg-white rounded-lg border border-gray-100 text-[#404752] font-medium text-sm hover:border-[#B8956B]/30 hover:text-[#252A32] transition-all"
              >
                {supplier}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
